import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * BNO cart-drawer bundle-overlap remove handler.
 *
 * Two entry points, both driven by snippets/bno-cart-bundle-notices.liquid:
 *
 *   1. Overlap "Remove them?" button ([data-bno-remove-loose]) — bundle already in
 *      cart alongside loose plugins. Clicking removes ALL loose `bno-legacy-plugins`
 *      lines that are included in the bundle in a single `/cart/update.js` request.
 *
 *   2. Upsell "Upgrade" button (the shared bundle add form inside
 *      [data-bno-cart-upsell]) — loose plugins in cart, bundle NOT yet in cart.
 *      Clicking adds the bundle (Horizon product-form -> /cart/add.js -> CartAddEvent)
 *      AND, once that add succeeds, removes the overlapping loose lines in the same
 *      user action, so the drawer ends with just the bundle. The loose line keys are
 *      captured from the upsell element's `data-loose-keys` at click time and applied
 *      after the add's CartAddEvent fires.
 *
 * Only the DRAWER Upgrade button triggers the auto-cleanup. The homepage hero Buy,
 * PDP add buttons, the PDP bundle-upsell section, and adding a loose plugin while the
 * bundle is in cart all leave the overlap "Remove them?" message as the safety net —
 * none of them sit inside [data-bno-cart-upsell], so none set a pending removal.
 *
 * Detection and the notice markup are pure Liquid; this file only mutates the cart on
 * an explicit user action. Every refresh reuses the theme's existing morph path: the
 * `/cart/update.js` call requests the drawer's section and dispatches a
 * CartUpdateEvent with `data.sections`, so `cart-items-component` morphs the drawer
 * and the cart icon updates its count — no hand-rolled DOM patch, no reload.
 *
 * Event delegation on `document` is used deliberately: the notice element is replaced
 * wholesale on every cart morph, so delegated listeners stay valid without re-binding.
 */

const REMOVE_BUTTON_SELECTOR = '[data-bno-remove-loose]';
const OVERLAP_SELECTOR = '[data-bno-cart-overlap]';
const UPSELL_SELECTOR = '[data-bno-cart-upsell]';
// The Upgrade button is the bundle add form's submit button, scoped to the upsell notice.
const UPSELL_ADD_BUTTON_SELECTOR = '[data-bno-cart-upsell] button[name="add"]';

/**
 * Loose line keys captured when the Upgrade button is clicked, pending removal once
 * the bundle add succeeds. Null when no upgrade is in flight.
 * @type {string[] | null}
 */
let pendingLooseKeys = null;

/**
 * @param {string | null | undefined} raw
 * @returns {string[]}
 */
function parseKeys(raw) {
  return (raw ?? '')
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
}

/**
 * Removes the given loose line keys in one `/cart/update.js` request and refreshes the
 * drawer via the theme's morph path.
 * @param {string[]} keys - The cart line keys to set to 0.
 * @param {HTMLElement} [button] - Optional trigger button to mark busy while in flight.
 */
async function removeLooseKeys(keys, button) {
  if (keys.length === 0) return;

  // Header section id — the same source component-cart-items.js uses so the returned
  // section HTML matches the drawer that morphs.
  const cartItems = document.querySelector('cart-items-component[data-section-id]');
  const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;

  /** @type {Record<string, number>} */
  const updates = {};
  for (const key of keys) updates[key] = 0;

  button?.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: sectionId,
        sections_url: window.location.pathname,
      }),
    });

    const cart = await response.json();

    // Let the theme handle the refresh: cart-items-component morphs the drawer from
    // data.sections; cart-icon sets the new count from data.itemCount.
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'bno-cart-notices', {
        source: 'bno-cart-notices',
        itemCount: cart.item_count,
        sections: cart.sections,
      })
    );
  } catch (error) {
    console.error('BNO: failed to remove loose bundle plugins', error);
    button?.removeAttribute('aria-busy');
  }
}

/** @param {MouseEvent} event */
function handleClick(event) {
  const target = /** @type {Element | null} */ (event.target);

  // 1. Overlap "Remove them?" button: remove the overlapping loose lines now.
  const removeButton = target?.closest?.(REMOVE_BUTTON_SELECTOR);
  if (removeButton instanceof HTMLButtonElement) {
    event.preventDefault();
    if (removeButton.getAttribute('aria-busy') === 'true') return;

    const notice = removeButton.closest(OVERLAP_SELECTOR);
    const keys = parseKeys(notice?.getAttribute('data-loose-keys'));
    if (keys.length === 0) return;

    removeLooseKeys(keys, removeButton);
    return;
  }

  // 2. Upsell "Upgrade" button: let the bundle add proceed (do NOT preventDefault) and
  //    stash the overlapping loose keys to remove once the add succeeds.
  const upgradeButton = target?.closest?.(UPSELL_ADD_BUTTON_SELECTOR);
  if (upgradeButton) {
    const upsell = upgradeButton.closest(UPSELL_SELECTOR);
    const keys = parseKeys(upsell?.getAttribute('data-loose-keys'));
    pendingLooseKeys = keys.length > 0 ? keys : null;
  }
}

/**
 * After the Upgrade button's bundle add resolves, remove the pending loose lines.
 * @param {CartUpdateEvent} event
 */
function handleCartUpdate(event) {
  if (!pendingLooseKeys) return;

  const data = /** @type {{ source?: string, didError?: boolean }} */ (event.detail?.data ?? {});
  // Wait for the add's OWN event (the bundle add dispatches from the product form).
  // Ignore our own removal event (source 'bno-cart-notices') and unrelated updates.
  if (data.source !== 'product-form-component') return;

  const keys = pendingLooseKeys;
  pendingLooseKeys = null;

  // If the add failed, abort the cleanup — the loose plugins must stay.
  if (data.didError) return;

  removeLooseKeys(keys);
}

document.addEventListener('click', handleClick);
// CartAddEvent (bundle add) and CartUpdateEvent both dispatch ThemeEvents.cartUpdate.
// Note CartUpdateEvent has no static `eventName`, so the raw ThemeEvents value is used.
document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
